// EPOCH 35 - Phase 3: CSR Context Switching Provider
// Manages when a CSR is acting on behalf of a user

import React, { useState, useEffect, useCallback, ReactNode } from 'react';
import { supabase } from '../supabase/client';
import { CSRContext, CSRContextUser, CSRContextType } from './CSRContext';

interface CSRContextProviderProps {
  children: ReactNode;
}

export const CSRContextProvider: React.FC<CSRContextProviderProps> = ({ children }) => {
  const [isCSR, setIsCSR] = useState(false);
  const [targetUser, setTargetUser] = useState<CSRContextUser | null>(null);
  const [availableUsers, setAvailableUsers] = useState<CSRContextUser[]>([]);
  const [loading, setLoading] = useState(true);

  const loadAvailableUsers = useCallback(async (csrId: string) => {
    try {
      // Get companies assigned to this CSR through company_csrs table
      const { data: companyAssignments } = await supabase
        .from('company_csrs')
        .select('company_id')
        .eq('csr_id', csrId);

      if (!companyAssignments || companyAssignments.length === 0) {
        setAvailableUsers([]);
        return;
      }

      const companyIds = companyAssignments.map(ca => ca.company_id);
      
      // Get company details
      const { data: companies } = await supabase
        .from('companies')
        .select('id, name')
        .in('id', companyIds);

      if (!companies || companies.length === 0) {
        setAvailableUsers([]);
        return;
      }

      // Get users from these companies
      const { data: users } = await supabase
        .from('users')
        .select('id, email, full_name, company_id')
        .in('company_id', companyIds)
        .eq('role', 'user')
        .order('full_name');

      if (users) {
        // Map company names to users
        const usersWithCompanies = users.map(user => ({
          ...user,
          company_name: companies.find(c => c.id === user.company_id)?.name || 'Unknown'
        }));
        setAvailableUsers(usersWithCompanies);
      }
    } catch (error) {
      console.error('Error loading available users:', error);
    }
  }, []);

  const checkCSRStatus = useCallback(async () => {
    try {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      if (!currentSession) {
        setLoading(false);
        return;
      }

      // Check if user is a CSR
      const { data: userData } = await supabase
        .from('users')
        .select('role')
        .eq('id', currentSession.user.id)
        .single();

      if (userData?.role === 'csr') {
        setIsCSR(true);
        await loadAvailableUsers(currentSession.user.id);
      }
    } catch (error) {
      console.error('Error checking CSR status:', error);
    } finally {
      setLoading(false);
    }
  }, [loadAvailableUsers]);

  useEffect(() => {
    checkCSRStatus();
  }, [checkCSRStatus]);

  const switchToUser = (user: CSRContextUser | null) => {
    setTargetUser(user);
    
    // Store in session storage for persistence across navigation
    if (user) {
      sessionStorage.setItem('csr_target_user', JSON.stringify(user));
    } else {
      sessionStorage.removeItem('csr_target_user');
    }
  };

  const clearContext = () => {
    setTargetUser(null);
    sessionStorage.removeItem('csr_target_user');
  };

  // Restore context from session storage on mount
  useEffect(() => {
    const storedUser = sessionStorage.getItem('csr_target_user');
    if (storedUser && isCSR) {
      try {
        const user = JSON.parse(storedUser);
        setTargetUser(user);
      } catch (error) {
        console.error('Error restoring CSR context:', error);
      }
    }
  }, [isCSR]);

  const value: CSRContextType = {
    isCSR,
    isActingAsUser: !!targetUser,
    targetUser,
    availableUsers,
    switchToUser,
    clearContext,
    loading
  };

  return (
    <CSRContext.Provider value={value}>
      {children}
    </CSRContext.Provider>
  );
};